<script setup lang="ts">
import {
    faAngleDown,
    faAngleUp,
    faArrowLeft,
    faBus,
    faCircleCheck,
    faPen,
    faPlane,
    faPlaneDeparture,
    faSpinner,
    faTrain,
    faTrashCan,
    faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import dayjs from 'dayjs';
import CancelModal from './CancelModal.vue';
// delete in future
// import AutoReserveItemsBottomSheet from './AutoReserveItemsBottomSheet.vue';
import AutoReservePaxDetails from './AutoReservePaxDetails.vue';
import RemoveItem from './RemoveItem.vue';
import {
    AutomaticReserveBillStatus,
    BaggageTypes,
    ClassOfServiceTypes,
    type PeopleResponse,
    SeatTypes,
    ServiceType,
} from '@clients/PricetrackApi';
import {
    classOfServiceEnumMap,
    type AutomaticReserveDemandResponseExtended,
} from '@store/auto-reserve';
import AutoReserveItem from '@components/ui/auto&panel_auto-reserve/AutoReserveItem.vue';
import type { Radio } from '#imports';

interface IProps {
    item: AutomaticReserveDemandResponseExtended;
    loading?: boolean;
}
const { loading, item } = defineProps<IProps>();
const emit = defineEmits<{
    refresh: [];
}>();
const authStore = useAuthStore();
const i18n = useI18n();
const autoReserveStore = useAutoReserveStore();
const router = useRouter();
const { isMobile } = useSize();
const expanded = ref(false);
const pax = ref<PeopleResponse[]>([]);
const detailsHeight = ref(0);
const paxLoading = ref(false);
const isCancelling = ref(false);
const details = ref<HTMLDivElement>();
let closeRemove = () => {};
const { open: cancel, close: closeCancel } = useModal({
    component: CancelModal,
    attrs: { reserveId: item.id || 0 },
});

const seatTypes = [
    {
        label: 'همه',
        value: SeatTypes.NUMBER_1,
    },
    {
        label: 'تک صندلی',
        value: SeatTypes.NUMBER_0,
    },
];

const baggageTypes = [
    {
        label: 'فرقی نمی‌کند',
        value: BaggageTypes.NUMBER_1,
    },
    {
        label: 'دارای بار رایگان',
        value: BaggageTypes.NUMBER_0,
    },
];

const reserveStatus = computed(() => {
    switch (item.billStatus) {
        case AutomaticReserveBillStatus.NUMBER_0:
            return {
                icon: faSpinner,
                pulse: true,
                text: `این ${serviceTitle.value} دارای ظرفیت شده و در فرایند صدور قرار گرفت.`,
                color: 'var(--yellow-500)',
            };
        case AutomaticReserveBillStatus.NUMBER_1:
            return {
                icon: faTriangleExclamation,
                text: 'خرید ناموفق',
                color: 'var(--red-500)',
            };
        case AutomaticReserveBillStatus.NUMBER_2:
            return {
                icon: faCircleCheck,
                text: 'بلیط برای شما صادر شد.',
                color: 'var(--green-500)',
            };
        default:
            return null;
    }
});

function editPax() {
    const { close, open } = usePopOver({
        component: RemoveItem,
        attrs: {
            items: paxItems.value,
            label: 'ویرایش مسافران',
            allTitle: 'رزرو',
            description: 'مسافرانی که قصد حذف رزرو آنان را دارید، انتخاب کنید.',
            itemsTitle: 'مسافران',
            loading: isCancelling.value,
            onRemove: removePax,
            onRemoveAll: () => cancelTotally(),
        },
    });
    open();
    closeRemove = close;
}

function periodFormat(from: string, to: string) {
    return `${dayConfig.js(from).format(
        formatter({
            format: 'short',
            includeWeekday: false,
            includeYear: false,
            includeDate: false,
            includeTime: true,
        })
    )} تا ${dayConfig.js(to).format(
        formatter({
            format: 'short',
            includeWeekday: false,
            includeYear: false,
            includeDate: false,
            includeTime: true,
        })
    )}`;
}

function editTrips() {
    if (tripItems.value.length) {
        const { close, open } = usePopOver({
            component: RemoveItem,
            attrs: {
                items: tripItems.value,
                allTitle: 'رزرو',
                description:
                    'پروازهایی که قصد دارید از رزرو حذف کنید، انتخاب کنید.',
                itemsTitle: 'پروازها',
                hasSelectAll: true,
                selectAllInitially: true,
                loading: isCancelling.value,
                onRemove: removeTrips,
                onRemoveAll: () => cancelTotally(),
            },
        });
        open();
        closeRemove = close;
    } else {
        cancelTotally();
    }
}

const paxItems = computed<Radio<number>[]>(() =>
    pax.value.map(passenger => ({
        label: `${passenger.persianFirstName || passenger.firstName} ${
            passenger.persianLastName || passenger.lastName
        }`,
        value: passenger.id || 0,
    }))
);
const tripItems = computed<Radio<number>[]>(
    () =>
        item.items?.map(item => ({
            label: `${item.providerName} ${item.serviceNumber} - (${dayConfig
                .js(item.departureTime)
                .format(
                    formatter({
                        format: 'long',
                        includeYear: true,
                    })
                )})`,
            value: item.id || 0,
        })) || []
);
const maxHeight = computed(() => {
    if (!expanded.value) {
        return '0';
    }
    // const { length } = item.packages;
    // const height =
    //     (length - 1) * 88 + // all cards except one * 88px
    //     140 + // the reserved card perhaps
    //     (length - 1) * 16 + // all gaps between cards
    //     20 + // padding-top
    //     20 + // padding-bottom
    //     detailsHeight.value;
    return `${3000}px`;
});
const serviceTitle = computed(() => {
    switch (item.reserveType) {
        case ServiceType.NUMBER_0:
            return 'پرواز';
        case ServiceType.NUMBER_1:
            return 'قطار';
        case ServiceType.NUMBER_2:
            return 'اتوبوس';
        default:
            return 'پرواز';
    }
});
const pathTypeIcon = computed(() => {
    switch (item.reserveType) {
        case ServiceType.NUMBER_0:
            return faPlaneDeparture;
        case ServiceType.NUMBER_1:
            return faTrain;
        case ServiceType.NUMBER_2:
            return faBus;
        default:
            return faPlaneDeparture;
    }
});
const serviceIcon = computed(() => {
    switch (item.reserveType) {
        case ServiceType.NUMBER_0:
            return { icon: faPlane, rotate: '180' } as const;
        case ServiceType.NUMBER_1:
            return { icon: faTrain };
        case ServiceType.NUMBER_2:
            return { icon: faBus };
        default:
            return undefined;
    }
});
const date = computed(() =>
    item.departureDate
        ? dayConfig.js(item.departureDate).format(
              formatter({
                  format: 'long',
                  includeYear: true,
                  includeWeekday: true,
              })
          )
        : ''
);

const reserveHeaders = defineHeader([
    {
        key: 'serviceType',
        title: 'نوع وسیله نقلیه',
    },
    {
        key: 'destination',
        title: i18n.t('ReserveDetails.destination'),
    },
    {
        key: 'departureDate',
        title: i18n.t('ReserveDetails.date'),
        disabled: () => !!item.routeDemand?.departureTimes?.length,
    },
    {
        key: 'id',
        title: i18n.t('ReserveDetails.number'),
    },
    {
        key: 'status',
        title: i18n.t('ReserveDetails.status'),
    },
    {
        key: 'totalFare',
        title: i18n.t('ReserveDetails.price'),
    },
]);

const filtersHeaders = defineHeader([
    {
        key: 'classOfService',
        title: 'کلاس',
        disabled: () => item.reserveType !== ServiceType.NUMBER_0,
    },
    {
        key: 'departureTimeEarlyThreshold',
        title: 'تعجیل مجاز',
        disabled: () => item.reserveType !== ServiceType.NUMBER_0,
    },
    {
        key: 'departureTimeDelayedThreshold',
        title: 'میزان تاخیر مجاز',
        disabled: () => item.reserveType !== ServiceType.NUMBER_0,
    },
    {
        key: 'interceptionThresholdInHour',
        title: 'حداقل زمان مانده به حرکت',
        disabled: () => !item.interceptionThresholdInHour,
    },
    {
        key: 'baggageType',
        title: 'نوع بار',
    },
    {
        key: 'seatType',
        title: 'نوع صندلی',
        disabled: () => item.reserveType !== ServiceType.NUMBER_2,
    },
]);

const dateHeaders = defineHeader([
    {
        key: 'departureDate',
        title: 'تاریخ',
    },
    {
        key: 'period',
        title: 'بازه زمانی',
    },
]);
const disabled = computed(() => item.status !== 3);
const proceedButtonTitle = computed(() => {
    switch (item.status) {
        case 0:
            return 'لغو شده';
        case 1:
            return 'در حال رهگیری';
        case 2:
            return 'در انتظار پرداخت'; // Never should happen
        case 3:
            return 'مشاهده رزرو';
        case 4:
            return 'ناموفق';
        case 5:
            return 'در انتظار افزایش اعتبار';
        default:
            return '';
    }
});

const statusTitle = computed(() => {
    switch (item.status) {
        case 0:
            return 'لغو شده';
        case 1:
            return 'در حال رهگیری';
        case 2:
            return 'در انتظار پرداخت'; // Never should happen
        case 3:
            return 'موفق';
        case 4:
            return 'ناموفق';
        case 5:
            return 'در انتظار افزایش اعتبار';
        default:
            return '';
    }
});
const leftIcon = computed(() => {
    if (disabled.value) {
        return undefined;
    }
    return faArrowLeft;
});
const rightIcon = computed(() => {
    if (item.status !== 1) {
        return undefined;
    }
    return { icon: faSpinner, pulse: true };
});

const classOfService = computed(() =>
    item.classOfService === ClassOfServiceTypes.NUMBER_0
        ? 'همه کلاس‌های نرخی'
        : classOfServiceEnumMap.find(
              ({ value: v }) => v === item.classOfService
          )?.title
);

const baggageType = computed(
    () =>
        baggageTypes.find(
            ({ value }) => item.routeDemand?.baggageType === value
        )?.label
);

const seatType = computed(
    () => seatTypes.find(({ value: v }) => v === item.seatType)?.label
);

const totalPrice = computed(() => formatMoney(item.totalPrice));
const customDate = computed(() =>
    item.routeDemand?.departureTimes
        ?.reduce(
            (prev, next) => {
                if (prev.length) {
                    const includedDate = prev.findIndex(({ departureDate }) =>
                        dayjs(next.from).isSame(departureDate, 'day')
                    );
                    if (includedDate >= 0) {
                        prev[includedDate].period.push(
                            `${periodFormat(next.from || '', next.to || '')}`
                        );
                        return prev;
                    } else {
                        return [
                            {
                                departureDate: dayjs(next.from).format(),
                                period: [
                                    `${periodFormat(next.from || '', next.to || '')}`,
                                ],
                            },
                            ...prev,
                        ];
                    }
                } else {
                    return [
                        {
                            departureDate: dayjs(next.from).format(formatter()),
                            period: [
                                `${periodFormat(next.from || '', next.to || '')}`,
                            ],
                        },
                    ];
                }
            },
            [] as {
                departureDate: string;
                period: string[];
            }[]
        )
        .map(({ departureDate, ...rest }) => ({
            departureDate: dayConfig.js(departureDate).format(
                formatter({
                    format: 'long',
                    includeYear: true,
                    includeWeekday: true,
                })
            ),
            ...rest,
        }))
);

async function getPax(force = false) {
    if (pax.value.length && !force) {
        return;
    }
    paxLoading.value = true;
    if (force) {
        expanded.value = !expanded.value;
    }
    pax.value = (await autoReserveStore.getPassengers(item.id || 0)).people;
    paxLoading.value = false;
}

async function removePax(passengers: number[]) {
    isCancelling.value = true;
    await autoReserveStore.removePassengers({
        requestBody: passengers,
        id: item.id || 0,
    });
    isCancelling.value = false;
    closeRemove();
    await getPax(true);

    nextTick(() => {
        detailsHeight.value = details.value?.clientHeight || 0;
    });
}

async function removeTrips(trips: number[]) {
    try {
        isCancelling.value = true;
        await autoReserveStore.cancelDemand({
            automaticReserveDemandId: item.id || 0,
            cancellationRequest: {
                itemIds: trips,
            },
        });
        isCancelling.value = false;
        closeRemove();
    } catch (e) {
        reportException(e);
    }
}

async function openDetailsDesktop() {
    await getPax();
    nextTick(() => {
        detailsHeight.value = details.value?.clientHeight || 0;
    });
    expanded.value = !expanded.value;
}

async function cancelTotally() {
    await closeRemove();
    nextTick(() => {
        setTimeout(() => {
            cancel({ reserveId: item.id || 0 }).then(result =>
                cancelDemand(result.args?.[0])
            );
        }, 200);
    });
}

const cancelDemand = async ({
    success,
    canceled,
    message,
}: {
    message?: string;
    success?: boolean;
    canceled?: boolean;
}) => {
    await closeCancel();
    setTimeout(() => {
        if (success) {
            swal({
                icon: 'success',
                text: 'رزرو با موفقیت لغو شد.',
                confirmButtonText: 'باشه',
            });
            emit('refresh');
        } else if (!canceled && message) {
            swal({
                icon: 'error',
                text: message,
                confirmButtonText: 'باشه',
            });
        }
    }, 200);
};
const proceed = () => {
    const mobile = authStore.userInfo?.mobile;
    router.push(`/support/tracking?billCode=${item.billCode}&mobile=${mobile}`);
};
</script>
<template>
    <div class="auto-reserve-card-root">
        <div
            class="tracking-card-root"
            :class="{ 'loading-skeleton': loading, expanded }"
        >
            <div class="label">
                <span>رزرو خودکار</span>
            </div>
            <div class="ticket-section">
                <div class="segment">
                    <div class="corporation-frame">
                        <div class="corporation-container">
                            <mr-icon
                                :icon="pathTypeIcon"
                                size="large"
                                color="var(--blue-400)"
                            />
                        </div>
                        <span class="span">مسیر {{ serviceTitle }}</span>
                    </div>
                    <div class="route">
                        <div class="from">
                            <span class="location">
                                {{ item.origin || '' }}
                            </span>
                        </div>
                        <div class="router">
                            <span class="line" />
                            <mr-icon
                                v-if="serviceIcon"
                                size="small"
                                :icon="serviceIcon.icon"
                                :rotation="serviceIcon.rotate"
                                color="var(--cool-gray-400)"
                            />
                            <span class="line" />
                        </div>
                        <div class="to">
                            <span class="location">
                                {{ item.destination || '' }}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="day">
                    <mr-button
                        variant="transparent"
                        class="expand-button-desktop"
                        :left-icon="expanded ? faAngleUp : faAngleDown"
                        :loading="paxLoading"
                        @click="openDetailsDesktop"
                    >
                        جزئیات و ویرایش مسافران
                    </mr-button>
                    <mr-typography
                        color="var(--cool-gray-700)"
                        :align="{
                            desktop: 'right',
                            mobile: 'left',
                        }"
                    >
                        {{
                            item.routeDemand?.departureTimes?.length ? '' : date
                        }}
                    </mr-typography>
                </div>
            </div>
            <mr-divider
                class="divider"
                color="var(--blue-100)"
                thickness="1px"
            />
            <div class="reserve-section">
                <mr-button
                    v-if="item.status === 1"
                    variant="transparent"
                    :right-icon="faTrashCan"
                    size="medium"
                    class="cancel"
                    @click="editTrips"
                >
                    حذف رزرو
                </mr-button>
                <mr-button
                    v-if="item.status === 1"
                    variant="transparent"
                    :right-icon="faPen"
                    size="medium"
                    class="cancel"
                    link="nuxt"
                    :href="`auto-reserve/${item.id}`"
                >
                    ویرایش رزرو
                </mr-button>
                <div class="spacer" />
                <div v-if="reserveStatus" class="message">
                    <mr-icon
                        :icon="reserveStatus.icon"
                        :color="reserveStatus.color"
                        :pulse="reserveStatus.pulse"
                    />
                    <span class="text">{{ reserveStatus.text }}</span>
                </div>
                <mr-button
                    size="medium"
                    :disabled
                    :variant="
                        item.status === 1 && isMobile ? 'transparent' : 'filled'
                    "
                    full-width
                    class="button"
                    :class="{ half: item.status === 1 }"
                    :left-icon
                    :right-icon
                    @click="proceed"
                >
                    {{ proceedButtonTitle }}
                </mr-button>
            </div>
            <div class="expand-mobile">
                <mr-divider />
                <mr-button
                    variant="transparent"
                    size="small"
                    class="expand-button-mobile"
                    :left-icon="expanded ? faAngleUp : faAngleDown"
                    :loading="paxLoading"
                    @click="openDetailsDesktop"
                >
                    جزئیات و ویرایش مسافران
                </mr-button>
            </div>
        </div>
        <div class="children-wrapper" :style="{ maxHeight }">
            <div class="wrapper">
                <div ref="details">
                    <AutoReservePaxDetails
                        :pax
                        :edit-enabled="item.status === 1"
                        @edit-pax="editPax"
                    />
                </div>
                <AutoReserveItem
                    v-for="(pkg, index) in item.packages"
                    :key="pkg.key"
                    :package="pkg"
                    :model-value="!!item.items?.[index]?.isReserved"
                    in-panel
                />
                <div v-if="customDate" class="box">
                    <h2>زمان بندی رزروخودکار</h2>
                    <mr-data-table
                        class="tables"
                        :headers="dateHeaders"
                        :items="customDate"
                    >
                        <template #item.period="{ value }">
                            <ul>
                                <li v-for="period in value" :key="period">
                                    {{ period }}
                                </li>
                            </ul>
                        </template>
                    </mr-data-table>
                </div>
                <div class="box">
                    <h2>مشخصات رزروخودکار</h2>
                    <mr-data-table :headers="reserveHeaders" :items="[item]">
                        <template #item.serviceType>
                            {{ serviceTitle }}
                        </template>
                        <template #item.status>
                            {{ statusTitle }}
                        </template>
                        <template #item.departureDate>
                            {{ date }}
                        </template>
                        <template #item.totalFare>
                            {{ totalPrice }}
                        </template>
                    </mr-data-table>
                </div>
                <div class="box">
                    <h2>فیلترهای رزروخودکار</h2>
                    <mr-data-table :headers="filtersHeaders" :items="[item]">
                        <template #item.classOfService>
                            {{ classOfService }}
                        </template>
                        <template
                            #item.departureTimeDelayedThreshold="{ value }"
                        >
                            {{ `${value} دقیقه` }}
                        </template>
                        <template #item.interceptionThresholdInHour="{ value }">
                            {{ `${value} ساعت` }}
                        </template>
                        <template #item.departureTimeEarlyThreshold="{ value }">
                            {{ `${value} دقیقه` }}
                        </template>
                        <template #item.baggageType>
                            {{ baggageType || '-' }}
                        </template>
                        <template #item.seatType>
                            {{ seatType }}
                        </template>
                    </mr-data-table>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.box {
    display: flex;
    flex-direction: column;
    gap: 8px;
    .tables {
        :deep() {
            td:nth-child(odd) {
                border-left: 2px solid usePalette($p-blue, $p-100);
            }
            tr:nth-child(odd) {
                border-top: 2px solid usePalette($p-blue, $p-100);
            }
            tr:first-child {
                border-top: none;
            }
        }
    }
    @include mobile-only {
        background: white;
        border-radius: 16px;
        padding: 16px;
        margin-bottom: 8px;
        gap: 16px;
        .email {
            width: 30vw;
            overflow: hidden;
            text-overflow: ellipsis;
            direction: ltr;
        }
    }
}

.expand-mobile {
    @include desktop-only {
        display: none;
    }
}
.auto-reserve-card-root {
    @include shadow();
    border-radius: 12px;
    .tracking-card-root {
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 12px;
        background-color: white;
        &.expanded {
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
            .label {
                border-top-left-radius: 0;
            }
        }
        &.loading-skeleton {
            height: 140px;
            @include mobile-only {
                height: 272px;
            }

            .router {
                display: none !important;
            }

            .reserve-section {
                display: none !important;
            }
        }

        @include mobile-only {
            flex-direction: column;
        }
        @include shadow;

        .label {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 2px;
            background-color: usePalette($p-yellow, $p-100);
            transform: rotate(180deg);
            writing-mode: vertical-lr;
            text-orientation: mixed;
            align-self: stretch;
            border-top-left-radius: 12px;
            border-bottom-left-radius: 12px;
            @include mobile-only {
                padding: 8px;
                transform: unset;
                writing-mode: unset;
                text-orientation: unset;
                border-top-right-radius: 12px;
                border-bottom-left-radius: 0;
            }

            span {
                @include tg-s($w5, usePalette($p-yellow, $p-700));
                display: flex;
                align-items: center;
            }
        }

        .ticket-section {
            align-self: stretch;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: center;
            flex-grow: 1;
            padding-block: 4px;
            @include desktop-only {
                @include f-gap(8px, column);
            }
            @include mobile-only {
                flex-direction: column-reverse;
                align-self: stretch;
                padding: 12px;
                @include f-gap(12px, column-reverse);
            }

            .segment {
                display: flex;
                align-items: center;
                justify-content: flex-end;
                @include mobile-only {
                    align-self: stretch;
                    @include f-gap(12px);
                }

                .corporation-frame {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 16px;
                    width: 140px;
                    @include mobile-only {
                        padding: 0;
                        width: auto;
                    }

                    .corporation-container {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }

                    .span {
                        @include tg-m($color: usePalette($p-cool-gray, $p-700));
                        @include mobile-only {
                            @include tg-s(
                                $w5,
                                usePalette($p-cool-gray, $p-700)
                            );
                        }
                    }
                }

                .route {
                    display: flex;
                    align-items: flex-start;
                    justify-content: flex-end;
                    padding: 16px;
                    @include f-gap(12px);
                    @include mobile-only {
                        flex-grow: 1;
                        padding: 0 0 0 8px;
                    }

                    .from,
                    .to {
                        display: flex;
                        flex-direction: column;
                        align-items: flex-start;
                        width: 70px;
                        @include mobile-only {
                            width: 52px;
                        }

                        .location {
                            @include tg-display4($w7);
                            @include mobile-only {
                                @include tg-xl($w7);
                            }
                        }
                    }

                    .router {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        @include f-gap(4px);
                        width: 150px;
                        height: 32px;
                        @include mobile-only {
                            width: 100%;
                        }

                        .line {
                            border-top: 2px dashed
                                usePalette($p-cool-gray, $p-500);
                            flex-grow: 1;
                        }
                    }
                }
            }

            .day {
                margin-top: auto;
                display: flex;
                align-items: center;
                justify-content: flex-end;
                padding-inline: 12px;
                @include f-gap(12px);
                @include mobile-only {
                    justify-content: space-between;
                    align-self: stretch;
                    padding: 0;
                }
                .expand-button-mobile {
                    @include desktop-only {
                        display: none;
                    }
                    white-space: nowrap;
                }
                .expand-button-desktop {
                    @include mobile-only {
                        display: none;
                    }
                }
                .date {
                    width: 148px;
                }
            }
        }

        .divider {
            display: none;
            @include mobile-only {
                display: flex;
            }
        }

        .reserve-section {
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            justify-content: flex-end;
            align-self: stretch;
            padding: 12px;
            @include f-gap(4px, column);
            width: 230px;
            @include mobile-only {
                width: 100%;
                flex-direction: row;
                flex-wrap: wrap;
            }
            .spacer {
                flex-grow: 1;
                @include mobile-only {
                    display: none;
                }
            }
            .cancel {
                @include mobile-only {
                    width: 50%;
                }
            }
            .button {
                @include mobile-only {
                    &.half {
                        width: 50%;
                    }
                }
            }
            .message {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                .text {
                    @include tg-m($w5, v-bind('reserveStatus?.color'));
                }
                @include mobile-only {
                    margin-bottom: 8px;
                }
            }
        }
    }
    .children-wrapper {
        transition: max-height 200ms ease;
        border-bottom-left-radius: 12px;
        border-bottom-right-radius: 12px;
        background-color: usePalette($p-gray, $p-100);
        overflow: hidden;
        .wrapper {
            display: flex;
            flex-direction: column;
            padding: 20px;
            gap: 12px;
        }
    }
}
</style>
